import { useMemo, useEffect, useState } from 'react';
import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { ApplicationAiResources } from './types';
import { AiResourcesForApplicationQuery } from './queries';
import { ConfigHelper } from '@devxdiscover/devhub-common';

export interface UseAiResourcesResult {
  resources: ApplicationAiResources | null;
  loading: boolean;
  error: Error | undefined;
  reload: () => void;
}

interface QueryData {
  resources: {
    application: ApplicationAiResources;
  };
}

/**
 * Fetches AI resources (use cases, agents, gen AI models, MCP servers) for the
 * given application from the Galileo supergraph via /api/galileo/query.
 *
 * The backend proxy (galileo.ts) handles SAT acquisition and forwarding to the
 * Galileo Router. When galileo.stubAiResources is true in the backend config, the proxy
 * returns stub data directly — allowing end-to-end flow validation before the
 * Flow and Snow subgraphs are live in fusionrage.
 * @param applicationId - The numeric application ID to fetch AI resources for
 */
export function useAiResources(applicationId: number): UseAiResourcesResult {
  const [resources, setResources] = useState<ApplicationAiResources | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const galileoClient = useMemo(
    () =>
      new ApolloClient({
        link: createHttpLink({
          uri: ConfigHelper.instance.apiUrl('galileo/query').href,
          credentials: 'include',
        }),
        cache: new InMemoryCache(),
      }),
    [],
  );

  const load = () => {
    setLoading(true);
    setError(undefined);
    galileoClient
      .query<QueryData>({
        query: AiResourcesForApplicationQuery,
        variables: { applicationId },
        fetchPolicy: 'network-only',
        // errorPolicy: 'all' allows partial data + subgraph errors to coexist.
        // Without this, any GraphQL error (e.g. a subgraph 404) rejects the
        // promise and discards valid partial data.
        errorPolicy: 'all',
      })
      .then(result => {
        setResources(result.data?.resources?.application ?? null);
      })
      .catch(e => {
        setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (applicationId) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  return { resources, loading, error, reload: load };
}
